import { UAParser } from "ua-parser-js";

class UserMediaService {
  static async checkCameraPermission() {
    try {
      const permissions = await navigator.permissions.query({
        name: "camera",
      });
      return permissions.state;
    } catch (error) {
      console.error("Error checking camera permission:", error);
      return "error";
    }
  }

  static async askCameraPermission() {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      return "granted";
    } catch (error) {
      console.error("Error requesting camera permission:", error);
      if (error instanceof DOMException) {
        return error.name === "NotAllowedError" ? "denied" : "error";
      }
      return "error";
    }
  }

  static async checkMicrophonePermission() {
    try {
      const permissions = await navigator.permissions.query({
        name: "microphone",
      });
      return permissions.state;
    } catch (error) {
      console.error("Error checking microphone permission:", error);
      return "error";
    }
  }

  static async askMicrophonePermission() {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return "granted";
    } catch (error) {
      console.error("Error requesting microphone permission:", error);
      if (error instanceof DOMException) {
        return error.name === "NotAllowedError" ? "denied" : "error";
      }
      return "error";
    }
  }

  static async hasCamera() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some((device) => device.kind === "videoinput");
    } catch (error) {
      console.error("Error checking for camera availability:", error);
      return false;
    }
  }

  static async hasMicrophone() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some((device) => device.kind === "audioinput");
    } catch (error) {
      console.error("Error checking for microphone availability:", error);
      return false;
    }
  }

  static async getUserAgents() {
    try {
      const parser = new UAParser();
      const result = parser.getResult();
      return {
        ua: result?.ua || null,
        browser_name: result?.browser?.name || null,
        browser_version: result?.browser?.version || null,
        os_name: result?.os?.name || null,
        os_version: result?.os?.version || null,
        device_type_name: result?.device?.type || "Desktop",
        // screen_resolution: `${window.screen.width}x${window.screen.height}`,
        screen_height: window.screen.height,
        screen_width: window.screen.width,

        // browser_major: result?.browser?.major || null,
        // cpu_architecture: result?.cpu?.architecture || null,
        // device_vendor: result?.device?.vendor || null,
        // device_model: result?.device?.model || null,
        // engine_name: result?.engine?.name || null,
        // engine_version: result?.engine?.version || null,
      };
    } catch (error) {
      return null;
    }
  }
  static async getLocation() {
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by this browser.");
      return { latitude: null, longitude: null };
    }

    return new Promise((resolve) => {
      let isResolved = false;

      const timeoutId = setTimeout(() => {
        if (!isResolved) {
          console.warn("User didn't respond in time for location permission.");
          resolve({ latitude: null, longitude: null });
        }
      }, 5000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!isResolved) {
            clearTimeout(timeoutId);
            isResolved = true;
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          }
        },
        (error) => {
          if (!isResolved) {
            console.error("Error getting location:", error);
            clearTimeout(timeoutId);
            isResolved = true;
            resolve({ latitude: null, longitude: null });
          }
        },
      );
    });
  }

  static async getUserMediaPermissionsStatus() {
    try {
      const [hasCamera, hasMicrophone] = await Promise.all([
        this.hasCamera(),
        this.hasMicrophone(),
      ]);

      const [cameraPermission, microphonePermission] = await Promise.all([
        this.checkCameraPermission(),
        this.checkMicrophonePermission(),
      ]);

      return {
        hasCamera,
        cameraPermissionGranted: cameraPermission === "granted",
        hasMicrophone,
        microphonePermissionGranted: microphonePermission === "granted",
      };
    } catch (error) {
      return {
        hasCamera: false,
        cameraPermissionGranted: false,
        hasMicrophone: false,
        microphonePermissionGranted: false,
      };
    }
  }
}

export default UserMediaService;
